'use client';

import { FunctionComponent, type ReactNode } from 'react';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { exhaustiveGuard } from '@/utils/function-utils';
import { IContentMediaItem } from '../../../template-data/IContentMedia';

import S from './styles.base.module.scss';

/**
 * The collection of hover styles that can be applied to media items.
 */
export type HoverStyle = 'grow' | 'opacity-grow' | 'none';

export interface IHoverManipulationProps {
  /**
   * The array of media items to add a hover style to.
   */
  media: IContentMediaItem;

  /**
   * The hover style to apply to the media.
   */
  hoverStyle?: HoverStyle;

  /**
   * The children to render.
   */
  children?: ReactNode;
}

/**
 * Gets the overarching class used to style the hover style block.
 * @param hoverStyle - The hover style string for the media.
 * @returns - The class name that represents the hover style block.
 */
const getClassByHoverStyle = (hoverStyle: HoverStyle): string => {
  switch (hoverStyle) {
    case 'grow':
      return S.grow;
    case 'opacity-grow':
      return S.opacityGrow;
    case 'none':
      return '';
    default:
      return exhaustiveGuard(
        hoverStyle,
        `Unknown hover style prop value: ${hoverStyle}`
      );
  }
};

/**
 * A component that applies a hover style to media items.
 */
export const HoverManipulation: FunctionComponent<IHoverManipulationProps> = ({
  media,
  children
}) => {
  // If the hover style is none, return the children as is.
  const hoverStyle = media.hoverStyle ?? 'none';
  if (hoverStyle === 'none') {
    return <>{children}</>;
  }
  // Otherwise, apply the hover style class to the wrapper div.
  return (
    <div className={classes(S.hoverStyle, getClassByHoverStyle(hoverStyle))}>
      {children}
    </div>
  );
};

export default HoverManipulation;
